import { SensorOrSubSensorInfo } from '@components/cockpit/panels/monitoring/monitoring.types';
import {
  toAutomatonSectionIconFeatures,
  toAutomatonSectionLineAndPointsFeatures,
} from '@components/map/layers/automatonSections/automatonSection.mapper';
import { toJammingAreaFeatures } from '@components/map/layers/groundStation/groundStation.mapper';
import {
  toPerimeterCameraAzimuthLineFeatures,
  toPerimeterCameraLabelFeatures,
  toPerimeterCameraSectorFeatures,
} from '@components/map/layers/perimeterCameras/perimeterCamera.mapper';
import {
  toMonitoringSensorIconFeatures,
  toMonitoringSensorLabelFeatures,
  toPerimeterLinkFeatures,
  toSensorIconFeature,
  toSensorLabelFeature,
  toSubSensorIconFeature,
} from '@components/map/layers/sensors/sensor.mapper';
import {
  selectSelectedDoubtCheckPerimeterCameraUniqueKey,
  selectSelectedDoubtCheckSegmentName,
} from '@redux/global/global.selector';
import {
  selectDisplayedPerimeterLinksKeysByMapId,
  selectSelectedSensorsUniqueKeysByMapId,
} from '@redux/maps/maps.selectors';
import { selectActualMapFilters, selectHiddenFilterKeys } from '@redux/settings/settings.selectors';
import { selectSpatialDrones, selectTacticalSituation } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectParameterWithMapId, selectParameterWithReplayMode } from '@utils/common.utils';
import { getHaversineDistance } from '@utils/distance.utils';
import { getSectionName, getSectionPerimeterCameraUniqueKeys } from '@utils/map/automatonSection.utils';
import {
  getIconBasicInfoFromSensor,
  getSensorSpriteIconName,
  isConfigDisplayedOnMap,
  isSensorDisplayedOnMap,
  isSensorWithSubSensors,
  splitSensors,
} from '@utils/map/sensor.utils';
import {
  isCameraActiveMonitoringSensor,
  isCameraConfigurationType,
  isCameraMonitoringSensor,
  isMovableCamera,
} from '@utils/sensors/camera/camera.utils';
import {
  isJamming,
  isNeutralActiveMonitoringSensorWithSectors,
  isNeutralInactiveMonitoringSensorWithSectors,
} from '@utils/sensors/neutral/neutral.utils';
import {
  getSensorUniqueKey,
  getSubSensorAndSensorConfigurationAndStatus,
  getSubSensorConfigsFromParent,
  getSubSensorStatusesFromParent,
  hasSpecificState,
  hasSubSensorsWithCoordinates,
  isActiveAutomaton,
  isActiveGroundStation,
  isActiveMonitoringSensor,
  isInactiveMonitoringSensor,
  isMonitoringSensorOfType,
  isSensorOrSubSensorConfigurationOfType,
  isSubSensorUniqueKeys,
  toAutomatonSection,
} from '@utils/sensors/sensors.utils';
import { isValidCoordinates } from '@utils/validation/coordinates.utils';
import { Feature, FeatureCollection } from 'geojson';
import { chain } from 'lodash';

import { TacticalSituation, Target } from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import {
  filterKeyToFeatureEnum,
  FilterTriggerEnum,
  filterTriggerToFilterKey,
  FilterTypeEnum,
  MapFilters,
} from '@/types/filters.types';
import { MapIdEnum, SensorUniqueKeys, SplitMonitoringSensors } from '@/types/map.types';
import {
  CameraType,
  NeutralSensorWithSectorsType,
  SensorConfiguration,
  SensorConfigurationByType,
  SensorOrSubSensorConfiguration,
  SensorWithSubSensorType,
  SubSensorConfiguration,
} from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  ActiveMonitoringSensorByType,
  AppStatus,
  C2Licence,
  CameraActiveMonitoringSensor,
  InactiveMonitoringEquipment,
  InactiveMonitoringSensor,
  InactiveMonitoringSensorByType,
  Monitoring,
  MonitoringEquipment,
  MonitoringSensor,
  MonitoringSensorByType,
  NeutralActiveMonitoringWithSectors,
  SensorFamilyLicence,
  SensorsSpecificStatus,
} from '@/types/sensor/monitoring.types';
import { SensorFamilyEnum, SpatialLocation } from '@/types/sensor/sensor.types';
import {
  AutomatonCabinet,
  AutomatonCabinetStatus,
  AutomatonPortalWithStatus,
  AutomatonSection,
  AutomatonSectionData,
  AutomatonSectionStatus,
  AutomatonSectionStatusEnumWithMaintenance,
  AutomatonSegment,
  AutomatonSegmentKey,
  AutomatonSensorConfiguration,
  AutomatonSensorStatus,
  CameraTrackingStatus,
  ConnectorStatusEnum,
  EquipmentStatus,
  EquipmentTypeEnum,
  HealthStatusEnum,
  JammingModule,
  ModuleTypeEnum,
  PerimeterCamera,
  SensorTypeEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';
import {
  SensorOrSubSensorStatusWithoutSection,
  SensorStatus,
  SubSensorStatusWithoutSection,
} from '@/types/sensor/status.types';

const selectMonitoring = createSelector(
  selectTacticalSituation,
  (state: TacticalSituation | null): Monitoring | null => state?.monitoring ?? null,
);

export const selectTotalMemoryUsage = createSelector(
  selectMonitoring,
  (state: Monitoring | null): number | null => state?.totalMemoryUsage ?? null,
);

export const selectMonitoringSensors = createSelector(
  selectMonitoring,
  (state: Monitoring | null): SensorsSpecificStatus | null => state?.monitoringSensors ?? null,
);

export const selectAppStatuses = createSelector(
  selectMonitoring,
  (state: Monitoring | null): AppStatus[] => state?.appStatuses ?? [],
);

export const selectAllMonitoringSensors = createSelector(
  selectMonitoringSensors,
  (state: SensorsSpecificStatus | null): MonitoringSensor[] => state?.sensors ?? [],
);

export const selectAllSensorOrSubSensorInfo = createSelector(
  selectAllMonitoringSensors,
  (monitoringSensors: MonitoringSensor[]): SensorOrSubSensorInfo[] =>
    monitoringSensors.flatMap(getSubSensorAndSensorConfigurationAndStatus),
);

export const selectSensorOrSubSensorInfoByUniqueKey = createSelector(
  [selectAllSensorOrSubSensorInfo, selectParameterWithReplayMode<string>],
  (SensorOrSubSensorInfoList: SensorOrSubSensorInfo[], uniqueKey: string): SensorOrSubSensorInfo | null =>
    SensorOrSubSensorInfoList.find(
      (sensorOrSubSensorInfo) => sensorOrSubSensorInfo.configuration.uniqueKey === uniqueKey,
    ) ?? null,
);

export const selectLicence = createSelector(
  selectMonitoring,
  (state: Monitoring | null): C2Licence | null => state?.licence ?? null,
);

export const selectSensorFamilyLicence = createSelector(
  [selectLicence, selectParameterWithReplayMode<SensorFamilyEnum>],
  (licence: C2Licence | null, sensorFamily: SensorFamilyEnum): SensorFamilyLicence | null =>
    licence?.sensorLicence.availableFamilies.find((f) => f.family === sensorFamily) ?? null,
);

export const selectActiveMonitoringSensors = createSelector(
  selectAllMonitoringSensors,
  (sensors: MonitoringSensor[]): ActiveMonitoringSensor[] => sensors.filter(isActiveMonitoringSensor),
);

export const selectActiveMonitoringSensorsWithValidPosition = createSelector(
  selectActiveMonitoringSensors,
  (monitoringSensors: ActiveMonitoringSensor[]): ActiveMonitoringSensor[] =>
    monitoringSensors.filter(
      (monitoringSensor) =>
        hasSubSensorsWithCoordinates(monitoringSensor.configuration) ||
        isValidCoordinates(monitoringSensor.configuration.sensorPosition),
    ),
);

export const selectFilteredActiveMonitoringSensorsWithValidPosition = createSelector(
  [selectActiveMonitoringSensorsWithValidPosition, selectHiddenFilterKeys],
  (monitoringSensors: ActiveMonitoringSensor[], hiddenKeys): ActiveMonitoringSensor[] => {
    if (!hiddenKeys.length) {
      return monitoringSensors;
    }
    const hiddenTypes = new Set<SensorTypeEnum>(
      hiddenKeys
        .map((key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.C_UAS_SENSOR))
        .filter(Boolean) as SensorTypeEnum[],
    );
    if (hiddenTypes.size === 0) {
      return monitoringSensors;
    }
    return monitoringSensors.filter((monitoringSensor) => !hiddenTypes.has(monitoringSensor.type));
  },
);

export const selectInactiveMonitoringSensors = createSelector(
  selectAllMonitoringSensors,
  (sensors: MonitoringSensor[]): InactiveMonitoringSensor[] => sensors.filter(isInactiveMonitoringSensor),
);

export const selectInactiveMonitoringSensorsWithValidPosition = createSelector(
  selectInactiveMonitoringSensors,
  (inactiveSensors: InactiveMonitoringSensor[]): InactiveMonitoringSensor[] =>
    inactiveSensors.filter(
      (sensor) =>
        hasSubSensorsWithCoordinates(sensor.configuration) || isValidCoordinates(sensor.configuration.sensorPosition),
    ),
);

export const selectFilteredInactiveMonitoringSensorsWithValidPosition = createSelector(
  [selectInactiveMonitoringSensorsWithValidPosition, selectHiddenFilterKeys],
  (inactiveSensors: InactiveMonitoringSensor[], hiddenKeys): InactiveMonitoringSensor[] => {
    if (!hiddenKeys.length) {
      return inactiveSensors;
    }
    const hiddenTypes = new Set<SensorTypeEnum>(
      hiddenKeys
        .map((key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.C_UAS_SENSOR))
        .filter(Boolean) as SensorTypeEnum[],
    );
    if (hiddenTypes.size === 0) {
      return inactiveSensors;
    }
    return inactiveSensors.filter((inactiveSensor) => !hiddenTypes.has(inactiveSensor.type));
  },
);

export const selectMonitoringSensorsWithValidPosition = createSelector(
  [selectActiveMonitoringSensorsWithValidPosition, selectInactiveMonitoringSensorsWithValidPosition],
  (activeSensors, inactiveSensors): MonitoringSensor[] => [...activeSensors, ...inactiveSensors],
);

export const selectFilteredMonitoringSensorsWithValidPosition = createSelector(
  [selectFilteredActiveMonitoringSensorsWithValidPosition, selectFilteredInactiveMonitoringSensorsWithValidPosition],
  (activeSensors, inactiveSensors): MonitoringSensor[] => [...activeSensors, ...inactiveSensors],
);

export const selectMonitoringSensorBySensorUniqueKey = createSelector(
  [selectFilteredMonitoringSensorsWithValidPosition, selectParameterWithReplayMode<string>],
  (monitoringSensors, uniqueKey): MonitoringSensor | null =>
    monitoringSensors.find((monitoringSensor) => getSensorUniqueKey(monitoringSensor.configuration) === uniqueKey) ??
    null,
);

export const selectSplitMonitoringSensors = createSelector(
  [selectFilteredMonitoringSensorsWithValidPosition, selectActualMapFilters],
  (monitoringSensors: MonitoringSensor[], mapFilters: MapFilters) =>
    splitSensors(
      monitoringSensors.filter(
        (sensor) => !isSensorOrSubSensorConfigurationOfType(sensor.configuration, SensorTypeEnum.AUTOMATON),
      ),
      mapFilters,
    ),
);

export const selectSensorIconFeatures = createSelector(
  selectSplitMonitoringSensors,
  (sensors: SplitMonitoringSensors) => toMonitoringSensorIconFeatures(sensors),
);

export const selectSensorLabelFeatures = createSelector(
  selectSplitMonitoringSensors,
  (sensors: SplitMonitoringSensors): Feature[] => toMonitoringSensorLabelFeatures(sensors),
);

export const selectSensorFeatures = createSelector(
  [selectSensorIconFeatures, selectSensorLabelFeatures],
  (sensorIcons: Feature[], sensorLabels: Feature[]): Feature[] => [...sensorIcons, ...sensorLabels],
);

export const selectSplitPerimeterMonitoringSensors = createSelector(
  [selectFilteredMonitoringSensorsWithValidPosition, selectActualMapFilters],
  (monitoringSensors: MonitoringSensor[], mapFilters: MapFilters) =>
    splitSensors(
      monitoringSensors.filter((monitoringSensor) =>
        isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.AUTOMATON),
      ),
      mapFilters,
    ),
);

export const selectPerimeterSensorIconFeatures = createSelector(
  selectSplitPerimeterMonitoringSensors,
  (sensors: SplitMonitoringSensors) => toMonitoringSensorIconFeatures(sensors),
);

export const selectPerimeterSensorLabelFeatures = createSelector(
  selectSplitPerimeterMonitoringSensors,
  (sensors: SplitMonitoringSensors): Feature[] => toMonitoringSensorLabelFeatures(sensors),
);

export const selectPerimeterSensorFeatures = createSelector(
  [selectPerimeterSensorIconFeatures, selectPerimeterSensorLabelFeatures],
  (sensorIcons: Feature[], sensorLabels: Feature[]): FeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: [...sensorIcons, ...sensorLabels],
    };
  },
);

export const selectAllSensorConfigurations = createSelector(
  [selectActiveMonitoringSensors, selectInactiveMonitoringSensors],
  (activeSensors: ActiveMonitoringSensor[], inactiveSensors: InactiveMonitoringSensor[]): SensorConfiguration[] => [
    ...activeSensors.map((monitoringSensor) => monitoringSensor.configuration),
    ...inactiveSensors.map((monitoringSensor) => monitoringSensor.configuration),
  ],
);

export const selectSensorConfigurationByUniqueKey = createSelector(
  [selectAllSensorConfigurations, selectParameterWithReplayMode<string | null>],
  (sensorConfigurations, uniqueKey): SensorConfiguration | null =>
    sensorConfigurations.find((sensor) => getSensorUniqueKey(sensor) === uniqueKey) ?? null,
);

export const selectSensorConfigurationById = createSelector(
  [selectAllSensorConfigurations, selectParameterWithReplayMode<number | null>],
  (sensorConfigurations, id): SensorConfiguration | null =>
    sensorConfigurations.find((sensor) => sensor.id === id) ?? null,
);

export const selectSensorOrSubSensorConfigurationByUniqueKeys = createSelector(
  [selectAllSensorConfigurations, selectParameterWithReplayMode<SensorUniqueKeys>],
  (sensorConfigurations, uniqueKeys): SensorOrSubSensorConfiguration | null => {
    if (isSubSensorUniqueKeys(uniqueKeys)) {
      return (
        sensorConfigurations
          .filter(isSensorWithSubSensors)
          .flatMap((sensor) => getSubSensorConfigsFromParent(sensor))
          .find(
            (subSensorConfiguration) => getSensorUniqueKey(subSensorConfiguration) === uniqueKeys.sensorUniqueKey,
          ) ?? null
      );
    }
    return sensorConfigurations.find((sensor) => getSensorUniqueKey(sensor) === uniqueKeys.sensorUniqueKey) ?? null;
  },
);

export const selectSensorStatusByUniqueKey = createSelector(
  [selectActiveMonitoringSensors, selectParameterWithReplayMode<string | null>],
  (monitoringSensors, key): SensorStatus | null =>
    monitoringSensors.find((monitoringSensor) => getSensorUniqueKey(monitoringSensor.status) === key)?.status ?? null,
);

export const selectSubSensorStatusByUniqueKey = createSelector(
  [selectActiveMonitoringSensors, selectParameterWithReplayMode<string | null>],
  (monitoringSensors, key): SubSensorStatusWithoutSection | null =>
    monitoringSensors
      .flatMap((monitoringSensor) => getSubSensorStatusesFromParent(monitoringSensor.status))
      .find((subSensorStatus) => getSensorUniqueKey(subSensorStatus) === key) ?? null,
);

const selectStatusByUniqueKeys = (_: RootState, replay: boolean, uniqueKeys: SensorUniqueKeys) =>
  selectSensorStatusByUniqueKey(_, replay, uniqueKeys.sensorUniqueKey);
const selectSubStatusByUniqueKeys = (_: RootState, replay: boolean, uniqueKeys: SensorUniqueKeys) =>
  selectSubSensorStatusByUniqueKey(_, replay, uniqueKeys.sensorUniqueKey);

export const selectSensorOrSubSensorStatusByUniqueKeys = createSelector(
  [selectParameterWithReplayMode<SensorUniqueKeys>, selectStatusByUniqueKeys, selectSubStatusByUniqueKeys],
  (uniqueKeys, status, subStatus): SensorOrSubSensorStatusWithoutSection | null => {
    if ('parentUniqueKey' in uniqueKeys) {
      return subStatus;
    }
    return status;
  },
);

export const selectSelectedSensorFeaturesFromActiveMonitoringSensors = createSelector(
  [
    (state, mapId: MapIdEnum) =>
      selectFilteredActiveMonitoringSensorsWithValidPosition(state, mapId === MapIdEnum.REPLAY),
    selectSelectedSensorsUniqueKeysByMapId,
  ],
  (monitoringSensors, selectedSensorsUniqueKeys): Feature[] =>
    selectedSensorsUniqueKeys.flatMap((sensorUniqueKeys) => {
      if (isSubSensorUniqueKeys(sensorUniqueKeys)) {
        const monitoringSensor = (monitoringSensors.find(
          (monitoringSensor) => getSensorUniqueKey(monitoringSensor.status) === sensorUniqueKeys.parentUniqueKey,
        ) ?? null) as ActiveMonitoringSensorByType<SensorWithSubSensorType> | null;
        const subSensorStatus =
          (monitoringSensor &&
            getSubSensorStatusesFromParent(monitoringSensor.status).find(
              (subSensorStatus) => getSensorUniqueKey(subSensorStatus) === sensorUniqueKeys.sensorUniqueKey,
            )) ??
          null;

        const subSensorConfig =
          (monitoringSensor &&
            getSubSensorConfigsFromParent(monitoringSensor.configuration).find(
              (subSensor) => getSensorUniqueKey(subSensor) === sensorUniqueKeys.sensorUniqueKey,
            )) ??
          null;

        if (!monitoringSensor?.status || !subSensorConfig) {
          return [];
        }

        return [
          toSubSensorIconFeature({
            parentSensorConfig: monitoringSensor.configuration,
            subSensorConfig,
            subSensorStatus,
          }),
          toSensorLabelFeature(sensorUniqueKeys, subSensorConfig.position, subSensorConfig.name, subSensorConfig.type),
        ];
      }

      const monitoringSensor =
        monitoringSensors.find(
          (monitoringSensor) => getSensorUniqueKey(monitoringSensor.status) === sensorUniqueKeys.sensorUniqueKey,
        ) ?? null;

      if (!monitoringSensor || !isSensorDisplayedOnMap(monitoringSensor)) {
        return [];
      }

      const { configuration, status } = monitoringSensor;

      return [
        toSensorIconFeature(
          sensorUniqueKeys,
          configuration.sensorPosition,
          configuration.type,
          configuration.active,
          configuration.maintenance,
          getSensorSpriteIconName(getIconBasicInfoFromSensor(monitoringSensor)),
          configuration.name,
          status.effectiveStatus,
          hasSpecificState(status) ? status.specificState : undefined,
        ),
        toSensorLabelFeature(sensorUniqueKeys, configuration.sensorPosition, configuration.name, configuration.type),
      ];
    }),
);

export const selectSelectedSensorFeaturesFromInactiveMonitoringSensors = createSelector(
  [
    (state, mapId: MapIdEnum) =>
      selectFilteredInactiveMonitoringSensorsWithValidPosition(state, mapId === MapIdEnum.REPLAY),
    selectSelectedSensorsUniqueKeysByMapId,
  ],
  (inactiveMonitoringSensors, selectedSensorsUniqueKeys): Feature[] =>
    selectedSensorsUniqueKeys.flatMap((sensorUniqueKeys) => {
      if (isSubSensorUniqueKeys(sensorUniqueKeys)) {
        const parentSensorConfig = (inactiveMonitoringSensors.find(
          (sensor) => getSensorUniqueKey(sensor) === sensorUniqueKeys.parentUniqueKey,
        )?.configuration ?? null) as SensorConfigurationByType<SensorWithSubSensorType> | null;

        const subSensorConfig =
          (parentSensorConfig &&
            (getSubSensorConfigsFromParent(parentSensorConfig) as SubSensorConfiguration[]).find(
              (subSensor) => getSensorUniqueKey(subSensor) === sensorUniqueKeys.sensorUniqueKey,
            )) ??
          null;

        if (
          !parentSensorConfig ||
          !subSensorConfig ||
          isSensorOrSubSensorConfigurationOfType(subSensorConfig, SubSensorTypeEnum.AUTOMATON_SEGMENT)
        ) {
          return [];
        }

        return [
          toSubSensorIconFeature({
            parentSensorConfig,
            subSensorConfig,
            subSensorStatus: null,
          }),
          toSensorLabelFeature(sensorUniqueKeys, subSensorConfig.position, subSensorConfig.name, subSensorConfig.type),
        ];
      }

      const sensorMonitoring =
        inactiveMonitoringSensors.find((sensor) => getSensorUniqueKey(sensor) === sensorUniqueKeys.sensorUniqueKey) ??
        null;

      if (!sensorMonitoring || !isConfigDisplayedOnMap(sensorMonitoring)) {
        return [];
      }

      const { type, active, configuration } = sensorMonitoring;

      return [
        toSensorIconFeature(
          sensorUniqueKeys,
          configuration.sensorPosition,
          type,
          active,
          configuration.maintenance,
          getSensorSpriteIconName(getIconBasicInfoFromSensor(sensorMonitoring)),
          configuration.name,
        ),
        toSensorLabelFeature(sensorUniqueKeys, configuration.sensorPosition, configuration.name, type),
      ];
    }),
);

export const selectSelectedSensorFeatures = createSelector(
  [selectSelectedSensorFeaturesFromActiveMonitoringSensors, selectSelectedSensorFeaturesFromInactiveMonitoringSensors],
  (selectedSensorFeaturesFromActiveMonitoringSensors, selectedSensorFeaturesFromInactiveMonitoringSensors): Feature[] =>
    [...selectedSensorFeaturesFromActiveMonitoringSensors, ...selectedSensorFeaturesFromInactiveMonitoringSensors]
      .map((feature) => {
        const openedCluster = feature.properties?.value.openedCluster;
        const newCoordinates = feature.properties?.value.tempCoordinates;
        if (newCoordinates) {
          return openedCluster
            ? {
                ...feature,
                geometry: {
                  ...feature.geometry,
                  coordinates: newCoordinates,
                },
              }
            : null;
        }
        return feature;
      })
      .filter(Boolean) as Feature[],
);

// TODO HLF#30437 Missing test cases
export const selectAutomatonSegmentStatusByKey = createSelector(
  [selectActiveMonitoringSensors, selectParameterWithReplayMode<AutomatonSegmentKey>],
  (monitoringSensors, key): AutomatonSectionStatusEnumWithMaintenance | null => {
    if (!key.segment) {
      return null;
    }
    const automatonStatus =
      monitoringSensors.find((monitoringSensor) => getSensorUniqueKey(monitoringSensor) === key.automaton) ?? null;
    const cabinetStatus =
      (automatonStatus as ActiveMonitoringSensorByType<SensorTypeEnum.AUTOMATON> | null)?.status.subSensorStatuses.find(
        (cabinet) => getSensorUniqueKey(cabinet) === key.cabinet,
      ) ?? null;

    return (
      (cabinetStatus?.subSensorStatuses
        .flatMap((sectionStatus) => [sectionStatus.upperSegment, sectionStatus.lowerSegment])
        .filter(Boolean)
        .find((segment) => segment?.uniqueKey === key.segment)
        ?.effectiveStatus as AutomatonSectionStatusEnumWithMaintenance | null) ?? null
    );
  },
);

export const selectAutomatonActiveMonitoringSensors = createSelector(
  selectActiveMonitoringSensors,
  (monitoringSensors): AutomatonSensorStatus[] =>
    monitoringSensors
      .filter((monitoringSensor) => isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.AUTOMATON))
      .map((monitoringSensors) => monitoringSensors.status),
);

export const selectAutomatonCabinetStatuses = createSelector(selectAutomatonActiveMonitoringSensors, (automatons) =>
  automatons.flatMap((automaton) => automaton.subSensorStatuses),
);

const selectSectionStatuses = createSelector(selectAutomatonCabinetStatuses, (cabinets): AutomatonSectionStatus[] =>
  cabinets.flatMap((cabinet) => cabinet.subSensorStatuses),
);

export const selectSectionStatusByName = createSelector(
  [selectSectionStatuses, selectParameterWithReplayMode<string>],
  (statuses, name): AutomatonSectionStatus | null => statuses.find((status) => status.name === name) ?? null,
);

export const selectLowerStatesByName = createSelector(
  [selectSectionStatuses, selectParameterWithReplayMode<string>],
  (statuses, name) => statuses.find((status) => status.name === name)?.lowerSegment?.states ?? null,
);

export const selectUpperStatesByName = createSelector(
  [selectSectionStatuses, selectParameterWithReplayMode<string>],
  (statuses, name) => statuses.find((status) => status.name === name)?.upperSegment?.states ?? null,
);

export const selectActiveCameraMonitoringSensors = createSelector(
  selectActiveMonitoringSensorsWithValidPosition,
  (state: ActiveMonitoringSensor[]): CameraActiveMonitoringSensor[] => state.filter(isCameraActiveMonitoringSensor),
);

export const selectCameraMonitoringSensors = createSelector(
  selectAllMonitoringSensors,
  (state: MonitoringSensor[]): MonitoringSensorByType<CameraType>[] => state.filter(isCameraMonitoringSensor),
);

export const selectCameraMonitoringSensorByUniqueKey = createSelector(
  [
    (state: RootState, replayMode: boolean, uniqueKey: string | null) => {
      return { monitoringSensors: selectCameraMonitoringSensors(state, replayMode), uniqueKey };
    },
  ],
  ({ monitoringSensors, uniqueKey }): MonitoringSensorByType<CameraType> | null =>
    monitoringSensors.find((monitoringSensor) => getSensorUniqueKey(monitoringSensor) === uniqueKey) ?? null,
);

export const selectMovableCameraActiveMonitoringSensors = createSelector(
  selectActiveCameraMonitoringSensors,
  (monitoringSensors: CameraActiveMonitoringSensor[]): CameraActiveMonitoringSensor[] =>
    monitoringSensors.filter(isMovableCamera),
);

export const selectTrackingStatesOfMovableCameras = createSelector(
  selectMovableCameraActiveMonitoringSensors,
  (monitoringSensors: CameraActiveMonitoringSensor[]): { uniqueKey: string; status: CameraTrackingStatus }[] =>
    monitoringSensors.map(({ status }) => ({
      uniqueKey: getSensorUniqueKey(status),
      status: status.trackingStatus,
    })),
);

export const selectAutomatonSectionAssociatedLadCameraName = createSelector(
  [selectMonitoringSensorsWithValidPosition, selectParameterWithReplayMode<AutomatonSection>],
  (monitoringSensors: MonitoringSensor[], section): string | null => {
    const cameraConfigurations = monitoringSensors
      .map((sensor) => sensor.configuration)
      .filter(isCameraConfigurationType);

    return (
      cameraConfigurations.find((cam) => getSensorUniqueKey(cam) === section.upperSegment?.ladCameraUniqueKey)?.name ??
      cameraConfigurations.find((cam) => getSensorUniqueKey(cam) === section.lowerSegment?.ladCameraUniqueKey)?.name ??
      null
    );
  },
);

export const selectClosestActiveMovableCamera = createSelector(
  [
    (state: RootState, replayMode: boolean, point: Coordinates | null) => ({
      monitoringSensors: selectMovableCameraActiveMonitoringSensors(state, replayMode),
      point,
    }),
  ],
  ({ monitoringSensors, point }): CameraActiveMonitoringSensor | null =>
    monitoringSensors.length > 0 && point !== null
      ? monitoringSensors
          .map((monitoringSensor) => ({
            monitoringSensor,
            distance: getHaversineDistance(monitoringSensor.configuration.sensorPosition, point),
          }))
          .reduce((p, c) => (p.distance < c.distance ? p : c), {
            monitoringSensor: monitoringSensors[0],
            distance: getHaversineDistance(monitoringSensors[0].configuration.sensorPosition, point),
          }).monitoringSensor
      : null,
);

export const selectCameraActiveMonitoringSensorByUniqueKey = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectActiveCameraMonitoringSensors(state, replayMode),
    (_: RootState, __: boolean, uniqueKey: string | null) => uniqueKey,
  ],
  (monitoringSensors, uniqueKey): CameraActiveMonitoringSensor | null =>
    monitoringSensors.find((monitoringSensor) => getSensorUniqueKey(monitoringSensor) === uniqueKey) ?? null,
);

export const selectTrackingStatusByUniqueKey = createSelector(
  selectCameraActiveMonitoringSensorByUniqueKey,
  (monitoringSensor: CameraActiveMonitoringSensor | null): CameraTrackingStatus | null =>
    monitoringSensor?.status.trackingStatus ?? null,
);

export const selectActiveGroundStationsConfigurations = createSelector(selectActiveMonitoringSensors, (activeSensors) =>
  activeSensors.filter(isActiveGroundStation).map((gcs) => gcs.configuration),
);

export const selectSkyJackerActiveMonitoring = createSelector(
  selectActiveMonitoringSensorsWithValidPosition,
  (monitoringSensors: ActiveMonitoringSensor[]): ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>[] =>
    monitoringSensors.filter((monitoringSensor) =>
      isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.SKY_JACKER),
    ),
);

export const selectHealthySkyJackerActiveMonitoring = createSelector(
  selectSkyJackerActiveMonitoring,
  (
    skyJackerMonitorings: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>[],
  ): ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>[] =>
    skyJackerMonitorings.filter((s) => s.status.healthStatus === HealthStatusEnum.HEALTHY),
);

export const selectActiveAutomatons = createSelector(selectActiveMonitoringSensors, (activeSensors) =>
  activeSensors.filter(isActiveAutomaton),
);

export const selectInactiveAutomatons = createSelector(selectInactiveMonitoringSensors, (inactiveSensors) =>
  inactiveSensors.filter((sensor) =>
    isSensorOrSubSensorConfigurationOfType(sensor.configuration, SensorTypeEnum.AUTOMATON),
  ),
);

export const selectAllAutomatonConfigurations = createSelector(
  [selectActiveAutomatons, selectInactiveAutomatons],
  (activeAutomatons, inactiveAutomatons) =>
    [...activeAutomatons, ...inactiveAutomatons].map(
      (automaton) => automaton.configuration,
    ) as AutomatonSensorConfiguration[],
);

export const selectAutomatonCabinetSegments = createSelector(
  selectAllAutomatonConfigurations,
  (automatons: AutomatonSensorConfiguration[]): AutomatonSegment[] =>
    automatons.flatMap((automaton: AutomatonSensorConfiguration) =>
      automaton.cabinets.flatMap((cabinet: AutomatonCabinet) => cabinet.segments),
    ),
);

const getPortalStatus = (
  cabinet: AutomatonCabinet,
  cabinetStatuses: AutomatonCabinetStatus[],
): AutomatonSectionStatusEnumWithMaintenance =>
  (cabinetStatuses.find((cs) => cs.uniqueKey === cabinet.uniqueKey)?.healthStatus ??
    ConnectorStatusEnum.UNREACHABLE) as unknown as AutomatonSectionStatusEnumWithMaintenance;

export const selectAutomatonCabinetPortals = createSelector(
  [selectAllAutomatonConfigurations, selectAutomatonCabinetStatuses],
  (
    automatons: AutomatonSensorConfiguration[],
    cabinetStatuses: AutomatonCabinetStatus[],
  ): AutomatonPortalWithStatus[] =>
    automatons.flatMap((automaton: AutomatonSensorConfiguration) =>
      automaton.cabinets.flatMap(
        (cabinet: AutomatonCabinet) =>
          cabinet.portals?.map((p) => {
            return {
              ...p,
              status: getPortalStatus(cabinet, cabinetStatuses),
            };
          }) ?? [],
      ),
    ),
);

export const selectAutomatonSections = createSelector(
  selectAllAutomatonConfigurations,
  (automatons: AutomatonSensorConfiguration[]): AutomatonSectionData[] =>
    automatons.flatMap((automaton) =>
      automaton.cabinets.flatMap((cabinet) =>
        chain(cabinet.segments)
          .filter(Boolean)
          .groupBy('nodeId')
          .mapValues((segments) => {
            const section = toAutomatonSection(segments);
            return {
              automaton,
              cabinet,
              section,
              perimeterCameraUniqueKeys: getSectionPerimeterCameraUniqueKeys(section),
            };
          })
          .values()
          .value(),
      ),
    ),
);

export const selectAutomatonSectionByName = createSelector(
  [selectAutomatonSections, selectParameterWithReplayMode<string>],
  (automatonSections, sectionName) =>
    automatonSections.find(({ section }) => getSectionName(section) === sectionName) ?? null,
);

export const selectSectionByPerimeterCameraKey = createSelector(
  [selectAutomatonSections, selectParameterWithReplayMode<string>],
  (sections, perimeterCameraKey) =>
    sections
      .filter((sectionData) => sectionData.perimeterCameraUniqueKeys.includes(perimeterCameraKey))
      .map((sectionData) => {
        return getSectionName(sectionData.section);
      }),
);

export const selectFilteredAutomatonSections = createSelector(
  [selectAllAutomatonConfigurations, selectAutomatonSections, selectActualMapFilters],
  (
    automatons: AutomatonSensorConfiguration[],
    sections: AutomatonSectionData[],
    mapFilters: MapFilters,
  ): AutomatonSectionData[] =>
    mapFilters['displayPerimeterSensorSegment']
      ? sections.filter((section) =>
          automatons.some((automaton) => getSensorUniqueKey(section.automaton) === getSensorUniqueKey(automaton)),
        )
      : [],
);

export const selectAutomatonCabinetSectionIconFeatures = createSelector(
  [
    selectFilteredAutomatonSections,
    selectSectionStatuses,
    selectAutomatonCabinetPortals,
    selectParameterWithReplayMode<number>,
  ],
  (sections, statuses, portals, zoom): FeatureCollection => ({
    type: 'FeatureCollection',
    features: toAutomatonSectionIconFeatures(sections, statuses, portals, zoom),
  }),
);

export const selectAutomatonCabinetSectionLineFeatures = createSelector(
  [selectFilteredAutomatonSections, selectSectionStatuses, selectParameterWithReplayMode<number>],
  (sections, statuses, zoom): FeatureCollection => ({
    type: 'FeatureCollection',
    features: toAutomatonSectionLineAndPointsFeatures(sections, statuses, zoom),
  }),
);

export const selectPerimeterLinkFeatures = createSelector(
  [
    (state: RootState, mapId: string) => selectSplitPerimeterMonitoringSensors(state, mapId === MapIdEnum.REPLAY),
    (state: RootState, mapId: string) => selectDisplayedPerimeterLinksKeysByMapId(state, mapId),
    (state: RootState, mapId: string) => selectAutomatonSections(state, mapId === MapIdEnum.REPLAY),
    selectActualMapFilters,
    selectParameterWithMapId<number>,
  ],
  (sensors: SplitMonitoringSensors, displayedPerimeterLinksKeys, automatonSections, mapFilters, zoom): Feature[] => {
    const automatonConfigurations = sensors.withSubSensors
      .map((monitoringSensor) => monitoringSensor.configuration)
      .filter((config): config is AutomatonSensorConfiguration =>
        isSensorOrSubSensorConfigurationOfType(config, SensorTypeEnum.AUTOMATON),
      );
    if (displayedPerimeterLinksKeys) {
      const automaton = automatonConfigurations.find(
        (automaton) => getSensorUniqueKey(automaton) === displayedPerimeterLinksKeys.automatonUniqueKey,
      );

      if (automaton) {
        if (displayedPerimeterLinksKeys.cabinetUniqueKey) {
          const cabinet = automaton.cabinets.find(
            (cabinet) => getSensorUniqueKey(cabinet) === displayedPerimeterLinksKeys.cabinetUniqueKey,
          );

          const sections = automatonSections
            .filter((sectionData) => {
              if (displayedPerimeterLinksKeys.sectionUniqueKey) {
                return getSectionName(sectionData.section) === displayedPerimeterLinksKeys.sectionUniqueKey;
              }
              return getSensorUniqueKey(sectionData.cabinet) === displayedPerimeterLinksKeys.cabinetUniqueKey;
            })
            .map((sectionData) => sectionData.section);

          if (cabinet && sections.length > 0) {
            return toPerimeterLinkFeatures(automaton, cabinet, sections, mapFilters, zoom);
          }
        } else {
          return automaton.cabinets.flatMap((cabinet) => {
            const sections = automatonSections
              .filter((sectionData) => getSensorUniqueKey(sectionData.cabinet) === getSensorUniqueKey(cabinet))
              .map((sectionData) => sectionData.section);
            if (sections.length > 0) {
              return toPerimeterLinkFeatures(automaton, cabinet, sections, mapFilters, zoom);
            }
            return [];
          });
        }
      }
    }
    return [];
  },
);
export const selectAllPerimeterCameras = createSelector(
  selectMonitoringSensors,
  (sensorsSpecificStatus): PerimeterCamera[] => sensorsSpecificStatus?.perimeterCameras ?? [],
);

export const selectAllValidPerimeterCameras = createSelector(
  selectAllPerimeterCameras,
  (perimeterCameras: PerimeterCamera[]): PerimeterCamera[] =>
    perimeterCameras.filter(
      (perimeterCamera) =>
        perimeterCamera.fov !== null &&
        perimeterCamera.range !== null &&
        perimeterCamera.azimuth !== null &&
        isValidCoordinates(perimeterCamera.coordinates),
    ),
);

export const selectAllValidFilteredPerimeterCameras = createSelector(
  [selectAllValidPerimeterCameras, selectHiddenFilterKeys],
  (perimeterCameras: PerimeterCamera[], hiddenKeys): PerimeterCamera[] => {
    const key = filterTriggerToFilterKey[FilterTypeEnum.PERIMETER_SENSOR][FilterTriggerEnum.PERIMETER_CAMERA];
    if (!key) {
      return perimeterCameras;
    }
    return hiddenKeys.includes(key) ? [] : perimeterCameras;
  },
);

export const selectPerimeterCameraUniqueKeysBySelectedDoubtCheckSegmentName = createSelector(
  [selectAutomatonCabinetSegments, selectSelectedDoubtCheckSegmentName],
  (segments, selectedSegmentName): string[] => {
    const selectedSegments = segments.filter((segment) => segment.name === selectedSegmentName);
    return [...new Set(selectedSegments.flatMap((s) => s.perimeterCameraUniqueKeys))];
  },
);

export const selectSelectedPerimeterCameraUniqueKeys = createSelector(
  [selectSelectedDoubtCheckPerimeterCameraUniqueKey, selectPerimeterCameraUniqueKeysBySelectedDoubtCheckSegmentName],
  (selectedDoubtCheckPerimeterCameraUniqueKey, selectedSegmentCameraUniqueKeys): string[] => {
    if (selectedDoubtCheckPerimeterCameraUniqueKey) {
      return [selectedDoubtCheckPerimeterCameraUniqueKey];
    }

    return selectedSegmentCameraUniqueKeys;
  },
);

export const selectAllReplayActivePerimCameras = createSelector(
  selectAllValidPerimeterCameras,
  (perimeterCameras: PerimeterCamera[]): PerimeterCamera[] =>
    perimeterCameras.filter((pericam) => pericam.sourceReplayUrl !== null),
);

export const selectPerimeterCameraSectorFeatures = createSelector(
  [selectAllValidFilteredPerimeterCameras, selectSelectedPerimeterCameraUniqueKeys],
  (perimeterCameras: PerimeterCamera[], selectedPerimeterCameraUniqueKeys): FeatureCollection => ({
    type: 'FeatureCollection',
    features: [
      ...toPerimeterCameraSectorFeatures(perimeterCameras, selectedPerimeterCameraUniqueKeys),
      ...toPerimeterCameraAzimuthLineFeatures(perimeterCameras),
    ],
  }),
);

export const selectPerimeterCameraLabelFeatures = createSelector(
  selectAllValidFilteredPerimeterCameras,
  (perimeterCameras: PerimeterCamera[]): FeatureCollection => ({
    type: 'FeatureCollection',
    features: toPerimeterCameraLabelFeatures(perimeterCameras),
  }),
);

export const selectDronesWithJammingInfo = createSelector(
  [selectSpatialDrones, selectActiveGroundStationsConfigurations],
  (droneTargets, gcsConfigurations) => {
    return droneTargets
      .map((drone) => {
        const gcsTrackId = drone.trackIds.find((tid) => tid.sensorType === SensorTypeEnum.GROUND_STATION);
        const gcsConfiguration =
          gcsTrackId && gcsConfigurations.find((gcs) => getSensorUniqueKey(gcs) === gcsTrackId.uniqueKey);
        const gcsDrone = gcsTrackId && gcsConfiguration?.drones.find((d) => d.systemId.toString() === gcsTrackId.id);
        const jammingModule = gcsDrone?.modules.find((module) => module.moduleType === ModuleTypeEnum.JAMMING);

        if (jammingModule === undefined) {
          return null;
        }
        const jammingTarget = droneTargets.find((drones) => drones.id === drone.technicalData?.jammingTargetId) ?? null;

        return { drone, jammingTarget, jammingModule };
      })
      .filter(Boolean) as {
      drone: Target<SpatialLocation>;
      jammingTarget: Target<SpatialLocation> | null;
      jammingModule: JammingModule;
    }[];
  },
);

export const selectJammingModuleById = createSelector(
  [selectDronesWithJammingInfo, selectParameterWithReplayMode<string | number>],
  (dronesWithJammingInfo, targetId): JammingModule | null => {
    return dronesWithJammingInfo.find((element) => element.drone.id === targetId)?.jammingModule ?? null;
  },
);

export const selectGcsJammingAreaFeatures = createSelector(
  [selectDronesWithJammingInfo],
  (dronesWithJammingInfo): Feature[] =>
    toJammingAreaFeatures(dronesWithJammingInfo.filter((dwj) => dwj.jammingTarget !== null)),
);

export const selectGroundStationFeatures = createSelector(
  selectGcsJammingAreaFeatures,
  (jammingAreaFeatures: Feature[]): FeatureCollection => ({
    type: 'FeatureCollection',
    features: jammingAreaFeatures,
  }),
);

export const selectActiveMonitoringEquipments = createSelector(
  selectMonitoringSensors,
  (state: SensorsSpecificStatus | null): EquipmentStatus[] => state?.activeEquipments ?? [],
);

export const selectInactiveMonitoringEquipments = createSelector(
  selectMonitoringSensors,
  (state: SensorsSpecificStatus | null): InactiveMonitoringEquipment[] => state?.inactiveEquipments ?? [],
);

export const selectAllMonitoringEquipments = createSelector(
  [selectActiveMonitoringEquipments, selectInactiveMonitoringEquipments],
  (
    activeMonitoringEquipments: EquipmentStatus[],
    inactiveMonitoringEquipments: InactiveMonitoringEquipment[],
  ): MonitoringEquipment[] => {
    return [...activeMonitoringEquipments, ...inactiveMonitoringEquipments];
  },
);

export const selectAllMonitoringEquipmentsBySensorReference = createSelector(
  [
    selectAllMonitoringEquipments,
    selectParameterWithReplayMode<{ sensorCode: string; sensorAppCode: string; equipmentType?: EquipmentTypeEnum }>,
  ],
  (monitoringEquipments: MonitoringEquipment[], { sensorCode, sensorAppCode, equipmentType }): MonitoringEquipment[] =>
    monitoringEquipments.filter(
      (monitoringEquipment) =>
        // Since we only have WEB_RELAY equipment this condition is not useful, but it'll be later
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (!equipmentType || monitoringEquipment.configuration.type === equipmentType) &&
        monitoringEquipment.configuration.sensorReferences.some(
          (sensorReference) =>
            sensorReference.sensorAppCode === sensorAppCode && sensorReference.sensorCode === sensorCode,
        ),
    ),
);

export const selectNeutralActiveMonitoringeSensorWithSectors = createSelector(
  selectActiveMonitoringSensors,
  (activeMonitoringSensors: ActiveMonitoringSensor[]): NeutralActiveMonitoringWithSectors[] =>
    activeMonitoringSensors.filter(isNeutralActiveMonitoringSensorWithSectors),
);

export const selectNeutralInactiveMonitoringSensorWithSectors = createSelector(
  selectInactiveMonitoringSensors,
  (
    inactiveMonitoringSensors: InactiveMonitoringSensor[],
  ): InactiveMonitoringSensorByType<NeutralSensorWithSectorsType>[] =>
    inactiveMonitoringSensors.filter(isNeutralInactiveMonitoringSensorWithSectors),
);

export const selectNeutralMonitoringSensorWithSectors = createSelector(
  [selectNeutralActiveMonitoringeSensorWithSectors, selectNeutralInactiveMonitoringSensorWithSectors],
  (
    activeMonitoringSensors: NeutralActiveMonitoringWithSectors[],
    inactiveMonitoringSensors: InactiveMonitoringSensorByType<NeutralSensorWithSectorsType>[],
  ): MonitoringSensorByType<NeutralSensorWithSectorsType>[] => [
    ...activeMonitoringSensors,
    ...inactiveMonitoringSensors,
  ],
);

export const selectJammingNeutralSensorStatus = createSelector(
  selectNeutralActiveMonitoringeSensorWithSectors,
  (monitoringSensors: NeutralActiveMonitoringWithSectors[]): NeutralActiveMonitoringWithSectors | null =>
    monitoringSensors.find((monitoring) => isJamming(monitoring.status)) ?? null,
);

export const selectIsNeutralSensorJamming = createSelector(
  selectNeutralActiveMonitoringeSensorWithSectors,
  (statuses: NeutralActiveMonitoringWithSectors[]): boolean => statuses.map((s) => s.status).some(isJamming),
);
